<template>
  <v-dialog
    :model-value="modelValue"
    max-width="900px"
    persistent
    scrollable
    data-testid="tcv2-history-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center pa-4">
        <v-icon class="mr-2">mdi-history</v-icon>
        <div class="flex-grow-1">
          <div class="text-h6">ประวัติการบันทึก</div>
          <div v-if="versionLabel" class="text-caption text-white-50">{{ versionLabel }}</div>
        </div>
        <v-btn icon variant="text" color="white" data-testid="tcv2-history-close-btn" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- Errors stay INSIDE the box on purpose: the snackbar belongs to the parent page, and a
             dialog that fires one would be talking over whatever the page is already saying. -->
        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="comfortable"
          class="ma-4 mb-0"
          data-testid="tcv2-history-error"
        >
          {{ error }}
        </v-alert>

        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="rows"
          :loading="loading"
          density="comfortable"
          :items-per-page="10"
          show-select
          item-value="snapshotID"
          :select-strategy="'page'"
          data-testid="tcv2-history-table"
        >
          <template v-slot:item.snapshotNo="{ item }">
            <div class="d-flex align-center flex-wrap ga-1">
              <v-chip size="small" variant="flat" :color="item.isCurrent ? 'success' : undefined">
                v{{ item.snapshotNo }}
              </v-chip>
              <v-chip
                v-if="item.isCurrent"
                size="x-small"
                variant="tonal"
                color="success"
                prepend-icon="mdi-check-circle"
              >
                Current
              </v-chip>
              <v-chip
                v-if="item.sourceAction === 'BASELINE'"
                size="x-small"
                variant="tonal"
                color="grey"
              >
                BASELINE
              </v-chip>
              <v-chip
                v-else-if="item.sourceAction === 'RESTORE'"
                size="x-small"
                variant="tonal"
                color="info"
              >
                RESTORE
                <v-tooltip v-if="item.restoredFromNo" activator="parent" location="top">
                  กู้คืนจาก v{{ item.restoredFromNo }}
                </v-tooltip>
              </v-chip>
              <!-- IMPORT is a real SourceAction here (a bundle brought this version in), so the row
                   must say so — otherwise it reads like an ordinary SAVE nobody remembers making. -->
              <v-chip
                v-else-if="item.sourceAction === 'IMPORT'"
                size="x-small"
                variant="tonal"
                color="deep-purple"
              >
                IMPORT
                <v-tooltip activator="parent" location="top">นำเข้าจากไฟล์ bundle</v-tooltip>
              </v-chip>
            </div>
          </template>

          <template v-slot:item.comment="{ item }">
            <div class="tcv2-history-comment">
              {{ item.comment || '-' }}
              <v-tooltip
                v-if="item.comment && item.comment.length > 40"
                activator="parent"
                location="top"
                max-width="320"
              >
                {{ item.comment }}
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.createdDate="{ item }">
            {{ fmtDate(item.createdDate) }}
          </template>

          <template v-slot:item.createdBy="{ item }">
            {{ item.createdBy || '-' }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon
              variant="text"
              size="small"
              density="comfortable"
              :disabled="item.isCurrent"
              data-testid="tcv2-history-restore"
              @click="openRestore(item)"
            >
              <v-icon size="18">mdi-restore</v-icon>
              <v-tooltip activator="parent" location="top">
                {{ item.isCurrent ? 'นี่คือเวอร์ชันปัจจุบัน' : 'กู้คืนรอบนี้' }}
              </v-tooltip>
            </v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-10" data-testid="tcv2-history-empty">
              <v-icon size="64" color="grey-lighten-1">mdi-history</v-icon>
              <p class="text-subtitle-1 mt-3 mb-1">ยังไม่มีประวัติการบันทึก</p>
              <p class="text-caption text-medium-emphasis mb-0">
                แก้ไข / เพิ่ม / ลบฟังก์ชันสักครั้ง แล้วประวัติจะเริ่มถูกเก็บ
              </p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-btn
          v-if="selected.length === 2"
          color="info"
          variant="flat"
          prepend-icon="mdi-compare"
          data-testid="tcv2-history-compare"
          @click="openCompare"
        >
          เทียบ 2 รอบที่เลือก
        </v-btn>
        <v-chip
          v-else-if="selected.length > 0"
          size="small"
          color="warning"
          variant="tonal"
          data-testid="tcv2-history-compare-hint"
        >
          เลือกให้ครบ 2 รอบเพื่อเทียบ
        </v-chip>
        <v-spacer />
        <v-btn variant="outlined" color="primary" data-testid="tcv2-history-close" @click="close">
          <v-icon start>mdi-close</v-icon>
          Close
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Restore confirmation — nested so it stacks over the list instead of replacing it. -->
    <v-dialog
      v-model="restoreDialog"
      max-width="600px"
      persistent
      data-testid="tcv2-history-restore-dialog"
    >
      <v-card>
        <v-card-title class="bg-primary text-white d-flex align-center pa-4">
          <v-icon class="mr-2">mdi-restore</v-icon>
          ยืนยันการกู้คืน
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert
            v-if="restoreError"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mb-4"
            data-testid="tcv2-history-restore-error"
          >
            {{ restoreError }}
          </v-alert>

          <v-alert type="info" variant="tonal" density="comfortable" class="mb-4">
            จะเขียนทับชุดสูตรปัจจุบันด้วยข้อมูลของ <strong>v{{ restoreNo }}</strong> —
            ประวัติเดิมยังอยู่ครบ และการกู้คืนจะถูกบันทึกเป็นรอบใหม่ จึงย้อนกลับได้อีก
          </v-alert>

          <v-alert
            v-if="versionActive"
            type="warning"
            variant="tonal"
            density="comfortable"
            icon="mdi-alert"
            class="mb-4"
          >
            เวอร์ชันนี้กำลังใช้งานจริง (ACTIVE) — การกู้คืนจะมีผลกับการคำนวณที่ใช้งานอยู่ทันที
          </v-alert>

          <v-textarea
            v-model="restoreComment"
            label="หมายเหตุ (ไม่บังคับ)"
            variant="outlined"
            density="comfortable"
            rows="3"
            maxlength="500"
            counter="500"
            hide-details="auto"
            data-testid="tcv2-history-restore-comment"
          />

          <v-checkbox
            v-if="versionActive"
            v-model="liveAck"
            data-testid="tcv2-history-live-ack"
            color="warning"
            density="comfortable"
            hide-details
            label="ฉันเข้าใจว่านี่คือเวอร์ชันที่ใช้งานจริง"
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="outlined" :disabled="restoring" data-testid="tcv2-history-restore-cancel" @click="closeRestore">
            <v-icon start>mdi-close</v-icon>
            ยกเลิก
          </v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="restoring"
            :disabled="versionActive && !liveAck"
            data-testid="tcv2-history-restore-confirm"
            @click="confirmRestore"
          >
            <v-icon start>mdi-restore</v-icon>
            ยืนยันกู้คืน
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <tax-calc-history-compare-v2-dialog
      v-model="compareDialog"
      :left-snapshot-id="compareIds[0]"
      :right-snapshot-id="compareIds[1]"
    />
  </v-dialog>
</template>

<script>
/**
 * Save-history dialog of ONE Tax Calculate V2 version — the list of snapshots taken on every write
 * of a formula (create / update / delete), with «restore» per row and «compare» across two ticked rows.
 *
 * Contract with the parent (the function-list page):
 *   - the parent owns the version identity (`calcVersionId`) and the snackbar;
 *   - this dialog reports errors inside itself (`v-alert`), never through a snackbar of its own;
 *   - `restored` fires AFTER the API call succeeded, carrying the restore result
 *     `{ calcVersionID, restoredFromNo, functionCount, warnings[] }` so the parent can reload the
 *     whole function list — a restore can add, remove and re-compile rows all at once.
 *
 * The list is (re)loaded every time the dialog opens, and again right after a successful restore
 * — a restore is itself recorded as a new snapshot, so the table must not show a stale «Current».
 *
 * Wire dates carry NO `Z` (Gotcha G10): `new Date(...)` on them reads as local time and shows 7h
 * early. Every date on this screen goes through `utils/thaiTime.js`.
 */
import taxCalculateV2Api from '@/services/taxCalculateV2Api';
import { fmtThaiDateTime } from '@/utils/thaiTime';
import TaxCalcHistoryCompareV2Dialog from '@/components/TaxCalcHistoryCompareV2Dialog.vue';

export default {
  name: 'TaxCalcHistoryV2Dialog',
  components: { TaxCalcHistoryCompareV2Dialog },
  props: {
    modelValue: { type: Boolean, default: false },
    /** calc version whose history is listed */
    calcVersionId: { type: Number, default: null },
    /** shown as the dialog subtitle (e.g. `V1 — สูตรภาษี 2568`) */
    versionLabel: { type: String, default: '' },
    /** true when this calc set is the ACTIVE one — gates the restore confirmation */
    versionActive: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'restored'],
  data() {
    return {
      loading: false,
      error: '',
      rows: [],
      /** array of `snapshotID` (v-data-table show-select binds item-value, not objects) */
      selected: [],
      compareDialog: false,
      compareIds: [null, null],
      restoreDialog: false,
      restoreTarget: null,
      restoreComment: '',
      liveAck: false,
      restoring: false,
      restoreError: '',
      headers: [
        { title: 'Version', key: 'snapshotNo', align: 'center', sortable: true, width: '150px' },
        { title: 'Comment', key: 'comment', align: 'start', sortable: false },
        { title: 'Date', key: 'createdDate', align: 'center', sortable: true, width: '190px' },
        { title: 'Created By', key: 'createdBy', align: 'center', sortable: false, width: '150px' },
        { title: 'Actions', key: 'actions', align: 'center', sortable: false, width: '100px' },
      ],
    };
  },
  computed: {
    /** `v{N}` of the row being restored — used by the confirm dialog's copy. */
    restoreNo() {
      return this.restoreTarget ? this.restoreTarget.snapshotNo : '';
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.load();
    },
  },
  methods: {
    fmtDate(value) {
      return fmtThaiDateTime(value, '-');
    },
    async load() {
      if (!this.calcVersionId) {
        this.rows = [];
        this.selected = [];
        return;
      }
      this.loading = true;
      this.error = '';
      try {
        const data = await taxCalculateV2Api.listHistory(this.calcVersionId);
        this.rows = Array.isArray(data) ? data : [];
        // Drop ticks that point at rows the reload no longer has (retention prune / restore).
        const alive = new Set(this.rows.map((r) => r.snapshotID));
        this.selected = this.selected.filter((id) => alive.has(id));
      } catch (e) {
        this.rows = [];
        this.selected = [];
        this.error = (e && e.message) || 'โหลดประวัติการบันทึกไม่สำเร็จ';
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    openCompare() {
      if (this.selected.length !== 2) return;
      // Order does not matter here — the compare dialog puts the lower SnapshotNo on the left.
      this.compareIds = [this.selected[0], this.selected[1]];
      this.compareDialog = true;
    },
    openRestore(item) {
      this.restoreTarget = item;
      this.restoreComment = `Restored from v${item.snapshotNo}`;
      this.liveAck = false;
      this.restoreError = '';
      this.restoreDialog = true;
    },
    closeRestore() {
      this.restoreDialog = false;
      this.restoreTarget = null;
      this.restoreComment = '';
      this.liveAck = false;
      this.restoreError = '';
    },
    async confirmRestore() {
      if (!this.restoreTarget || this.restoring) return;
      if (this.versionActive && !this.liveAck) return;
      this.restoring = true;
      this.restoreError = '';
      try {
        const comment = (this.restoreComment || '').trim();
        const result = await taxCalculateV2Api.restoreSnapshot(this.restoreTarget.snapshotID, {
          comment: comment || null,
        });
        this.$emit('restored', result);
        this.closeRestore();
        await this.load();
      } catch (e) {
        this.restoreError = (e && e.message) || 'กู้คืนไม่สำเร็จ';
      } finally {
        this.restoring = false;
      }
    },
  },
};
</script>

<style scoped>
.tcv2-history-comment {
  max-width: 340px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.text-white-50 {
  opacity: 0.8;
}
</style>
